import React from 'react'
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Table, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon,  PenBoxIcon, PlusIcon } from 'lucide-react';
import { format } from "date-fns";
import CustomButton from '../../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import AddUpdateCountryModal from '../../components/Masters/CountryMaster/AddUpdateCompanyModal';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { Search } from 'lucide-react';

const CountryMaster = () => {
  const [country, setCountry] = useState([]);
  const dispatch = useDispatch();

  const [isAddUpdateCountryModalOpen, setIsAddUpdateCountryModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState(null);

  // Pagination and filter states
  const [totalCountry, setTotalCountry] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  const getAllCountry = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const response = await api.get('/admin/getCountry', {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder
        }
      });
      setCountry(response.data.data);
      setTotalCountry(response.data.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch Countries',
        type: 'error',
      }));
    }
  }

  useEffect(() => {
    getAllCountry();
  }, [page, rowsPerPage, filters]);

  const debouncedSearch = debounce((value) => { 
    setPage(0);
    setFilters(prev => ({ ...prev, search: value })); setPage(0); 
  }, 500);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const handleRemove = async (id) => {
    const countryType = country.find(type => type.id === id);
    setCountryToDelete(countryType);
    setDeleteDialogOpen(true);
  }

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      const response = await api.delete(`/admin/deleteCountry/${countryToDelete.id}`);
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: 'Country deleted successfully',
          type: "success"
        }));
        getAllCountry();
      } else {
        dispatch(showNotification({
          message: 'Failed to delete country',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error deleting country:', error);
      dispatch(showNotification({
        message: 'An error occurred while deleting',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
      setCountryToDelete(null);
    }
  }

  const handleActivateDeactivate = async (user) => {
    try {
      setIsSubmitting(true);
      const response = await api.post(`/admin/activateDeactivateCountry/${user.id}`, {
        is_active: !user.is_active
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: user.is_active ? 'Country deactivated successfully' : 'Country activated successfully',
          type: "success"
        }));
        getAllCountry();
      } else {
        dispatch(showNotification({
          message: user.is_active ? 'Failed to deactivate country' : 'Failed to activate country',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error activating/deactivating country:', error);
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleSortByChange = (value) => {
    setPage(0);
    setFilters(prev => ({ ...prev, sortBy: value }));
  };

  const handleSortOrderChange = (value) => {
    setPage(0);
    setFilters(prev => ({ ...prev, sortOrder: value }));
  };

  const handleEdit = async (user) => {
    setSelectedCountry(user);
    setIsAddUpdateCountryModalOpen(true);
  }

  return (
    <>
      <PageHeader title="Country">
        <div className="flex justify-end gap-5 items-center mt-5">
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search countries..."
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => debouncedSearch(e.target.value)}
              className="min-w-[300px]"
            />


            {/* Sort By */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => handleSortByChange(e.target.value)}
              >
                <MenuItem value="created_at">Created Date</MenuItem>
                <MenuItem value="country_name">Country Name</MenuItem>
                <MenuItem value="country_code">Country Code</MenuItem>
                <MenuItem value="country_currency_code">Country Currency Code</MenuItem>
                <MenuItem value="telephone_code">Telephone Code</MenuItem>
                <MenuItem value="country_currency_name">Country Currency Name</MenuItem>
              </Select>
            </FormControl>

            {/* Sort Order */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Order</InputLabel>
              <Select
                value={filters.sortOrder}
                label="Order"
                onChange={(e) => {
                  setPage(0);
                  handleSortOrderChange(e.target.value)
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </div>
          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Add Country</>}
            click={() => setIsAddUpdateCountryModalOpen(true)}
          />
        </div>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Country Name</TableCell>
                <TableCell>Country Code</TableCell>
                <TableCell>Country Currency Code</TableCell>
                <TableCell>Country Currency Name</TableCell>
                <TableCell>Telephone Code</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {country?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.country_name}</TableCell>
                  <TableCell>{user.country_code}</TableCell>
                  <TableCell>{user.country_currency_code}</TableCell>
                  <TableCell>{user.country_currency_name}</TableCell>
                  <TableCell>{user.telephone_code}</TableCell>
                  <TableCell>
                    {user.created_date ? format(new Date(user.created_date), "dd-MM-yyyy") : ""}
                  </TableCell>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(user)}
                      sx={{
                        backgroundColor: '#16A34A',
                        color: 'white',
                        '&:hover': { backgroundColor: '#00bf46' },
                        p: '8px',
                      }}
                    >
                      <PenBoxIcon size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(user.id)}
                      sx={{
                        backgroundColor: '#df3e3e',
                        color: 'white',
                        '&:hover': { backgroundColor: '#ff0000' },
                        p: '8px',
                      }}
                    >
                      <DeleteIcon size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={7}
                  count={totalCountry}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) => 
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Dialog
          open={isAddUpdateCountryModalOpen}
          onClose={() => { setIsAddUpdateCountryModalOpen(false); setSelectedCountry(null) }}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedCountry ? "Edit Country" : "Add New Country"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdateCountryModalOpen(false); setSelectedCountry(null) }}
            />
          </div>
          <DialogContent >
            <AddUpdateCountryModal
              refetch={getAllCountry}
              onClose={() => {
                setIsAddUpdateCountryModalOpen(false);
                setSelectedCountry(null);
              }}
              countryId={selectedCountry?.id}
              initialData={selectedCountry}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the equipment type "{countryToDelete?.country_name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Cancel"
                disabled={isSubmitting}
                click={() => setDeleteDialogOpen(false)}
              />
              <CustomButton
                className="bg-red-600 hover:bg-red-700 text-white"
                click={confirmDelete}
                text='Delete'
                disabled={isSubmitting}
              />
            </div>
          </DialogActions>
        </Dialog>
      </PageHeader>
    </>
  )
}

export default CountryMaster